import { MovableDialog } from '@/components/shared/MovableDialog';
// Phase phase-review-surface-strengthen — Force-confirm friction dialog.
//
// Replaces the previous single-checkbox + window.confirm() flow for
// admin-level force-confirmation of unclassified documents. The prior
// design let admins bypass the classification review in one click.
//
// This dialog requires:
//   1. A typed reason (≥10 characters) explaining why the force is needed.
//   2. An explicit checkbox acknowledging the action is recorded in the
//      audit log as a force-confirm event.
//
// These two requirements together constitute deliberate, friction'd action
// rather than a reflexive click. The audit trail itself is written by the
// backend when the `?force=true` flag is passed — this dialog just
// ensures the admin has consciously authorised it.

import { useState, type ReactElement } from 'react';

export interface ForceConfirmDialogProps {
  readonly unclassifiedCount: number;
  readonly classifiedCount: number;
  /** Called with the typed reason when the admin confirms. */
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}

const MIN_REASON_LEN = 10;

export function ForceConfirmDialog({
  unclassifiedCount,
  classifiedCount,
  onConfirm,
  onCancel,
}: ForceConfirmDialogProps): ReactElement {
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const reasonOk = reason.trim().length >= MIN_REASON_LEN;
  const canConfirm = reasonOk && acknowledged;

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="submission.forceConfirm"
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#B91C1C' }}>
          <span style={{ fontSize: 20 }} aria-hidden="true">⚠️</span>
          Admin override: force-confirm unclassified documents
        </span>
      }
      ariaLabel="Force-confirm override"
      testId="force-confirm-dialog"
      width={480}
      zIndex={9999}
    >
      <>

        {/* Warning */}
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.40)',
            borderRadius: 5,
            fontSize: 13,
            color: '#7F1D1D',
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          <strong>This bypasses the AI classification review.</strong>
          {' '}You are about to confirm{' '}
          <strong>{unclassifiedCount} unclassified</strong> document
          {unclassifiedCount === 1 ? '' : 's'} without a verified section assignment.
          {classifiedCount > 0 && (
            <> The call also confirms <strong>{classifiedCount} classified</strong> document
            {classifiedCount === 1 ? '' : 's'}.</>
          )}
          {' '}This action is permanently recorded in the audit log.
        </div>

        {/* Reason input */}
        <label
          htmlFor="force-confirm-reason"
          style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}
        >
          Reason for override <span style={{ color: '#B91C1C' }}>*</span>
          <span style={{ fontWeight: 400, color: '#6B7280', marginLeft: 8, fontSize: 12 }}>
            (min {MIN_REASON_LEN} characters)
          </span>
        </label>
        <textarea
          id="force-confirm-reason"
          data-testid="force-confirm-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Documents are placeholder files for this submission; AI classification is not applicable."
          style={{
            width: '100%',
            padding: '8px 10px',
            fontSize: 13,
            borderRadius: 4,
            border: `1px solid ${reasonOk ? '#86EFAC' : '#D1D5DB'}`,
            fontFamily: 'inherit',
            resize: 'vertical',
            marginBottom: 4,
            boxSizing: 'border-box',
          }}
        />
        <div style={{ fontSize: 11, color: reason.trim().length > 0 && !reasonOk ? '#B91C1C' : '#9CA3AF', marginBottom: 14 }}>
          {reason.trim().length}/{MIN_REASON_LEN} minimum characters
        </div>

        {/* Acknowledgment checkbox */}
        <label
          data-testid="force-confirm-ack-label"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            fontSize: 13,
            color: '#374151',
            cursor: 'pointer',
            marginBottom: 20,
            lineHeight: 1.45,
          }}
        >
          <input
            type="checkbox"
            data-testid="force-confirm-ack-checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            style={{ marginTop: 2, cursor: 'pointer', accentColor: '#B91C1C', flexShrink: 0 }}
          />
          <span>
            I understand this override will be recorded in the audit log as a{' '}
            <strong>force-confirm event</strong>, distinct from a normal human review confirmation.
          </span>
        </label>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-testid="force-confirm-cancel"
            onClick={onCancel}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              background: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: 4,
              cursor: 'pointer',
              color: '#374151',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="force-confirm-submit"
            onClick={() => {
              if (canConfirm) onConfirm(reason.trim());
            }}
            disabled={!canConfirm}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 700,
              background: canConfirm ? '#B91C1C' : '#E5E7EB',
              color: canConfirm ? '#fff' : '#9CA3AF',
              border: 'none',
              borderRadius: 4,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}
          >
            Force-confirm {unclassifiedCount} unclassified
          </button>
        </div>
      </>
    </MovableDialog>
  );
}
